import DropLanguage from './drop-language.component';
import { useNavigate, Link } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PROFILES, STORAGE } from '../../constants';
import { LocalStorageService } from '../../services/local-storage.service';
import { AlertService } from '../../services/alert.service';
import Icon from '../../components/forms/icon.component';
import { useSession } from '../../hooks/use-session.hook';
function Header() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [session, setSession] = useState(() => LocalStorageService.get(STORAGE.SESSION));
  const { accounts, selectedAccount } = useSession();

  const logout = useCallback(() => {
    LocalStorageService.clear();
    navigate('/auth/login');
  }, [navigate]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === STORAGE.SESSION) {
        const newSession = LocalStorageService.get(STORAGE.SESSION);
        setSession(newSession);

        if (!newSession) {
          navigate('/auth/login');
          AlertService.toastWarning(t('general.session-expired'));
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [navigate]);

  return (
    <>
      <div
        id="kt_header"
        className="header"
        data-kt-sticky="true"
        data-kt-sticky-name="header"
        data-kt-sticky-offset="{default: '200px', lg: '300px'}"
      >
        <div className="container-fluid d-flex align-items-stretch justify-content-between">
          <div className="d-flex align-items-center flex-grow-1 flex-lg-grow-0">
            <div className="d-flex align-items-center d-lg-none">
              <div
                className="btn btn-icon btn-active-color-primary ms-n2 me-1"
                id="kt_aside_toggle"
              >
                <Icon icon="ki-abstract-14" size="fs-1" />
              </div>
            </div>
            <Link to="/dashboard" className="d-lg-none">
              <img alt="Logo" src="/assets/media/logos/logo-c.svg" className="mh-40px" />
            </Link>
            <div
              className="btn btn-icon w-auto ps-0 btn-active-color-primary d-none d-lg-inline-flex me-2 me-lg-5 active"
              data-kt-toggle="true"
              data-kt-toggle-state="active"
              data-kt-toggle-target="body"
              data-kt-toggle-name="aside-minimize"
            >
              <Icon icon="ki-black-left-line" size="fs-2x" className="fw-bolder rotate-180" />
            </div>
          </div>
          <div className="d-flex align-items-stretch justify-content-between flex-lg-grow-1">
            <div className="d-flex align-items-stretch me-1"></div>
            <div className="d-flex align-items-stretch flex-shrink-0">
              <div className="d-flex align-items-center ms-1 ms-lg-2">
                <div
                  className="d-flex align-items-center ms-2 ms-lg-3"
                  id="kt_header_user_menu_toggle"
                >
                  {selectedAccount && Array.isArray(accounts) && (
                    <div className="d-flex align-items-center fs-3 text-gray800 me-10">
                      {t('templates.wa.account')}:{' '}
                      <span className="text-hover-primary text-primary ms-2">
                        {accounts.find(
                          (acc: any) => acc?.account?._id === selectedAccount?.account_id
                        )?.account?.name || 'Desconocida'}
                      </span>
                    </div>
                  )}
                  <div
                    className="cursor-pointer symbol symbol-50px symbol-circle symbol-lg-50px"
                    data-kt-menu-trigger="{default: 'click', lg: 'hover'}"
                    data-kt-menu-attach="parent"
                    data-kt-menu-placement="bottom-end"
                  >
                    <img alt="profile-picture" src="/assets/media/avatars/av1.png" />
                    <div className="bg-primary position-absolute border border-4 border-body h-15px w-15px rounded-circle translate-middle start-100 top-100 ms-n3 mt-n3"></div>
                  </div>
                  <div
                    className="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg menu-state-color fw-semibold py-4 fs-6 w-375px"
                    data-kt-menu="true"
                  >
                    <div className="card-toolbar">
                      <div className="d-flex align-items-center ms-1 ms-lg-2">
                        <a
                          className="btn btn-icon btn-active-light-primary w-40px h-40px w-md-40px h-md-40px"
                          data-kt-menu-attach="parent"
                          href="#"
                          data-kt-menu-placement="left-start"
                          data-kt-menu-trigger="{default:'click', lg: 'hover'}"
                        >
                          <Icon
                            icon="ki-setting-2"
                            size="fs-2x"
                            className="theme-light-show"
                            type="dark"
                          />
                          <Icon
                            icon="ki-setting-2"
                            size="fs-2x"
                            className="theme-dark-show"
                            type="dark"
                          />
                        </a>
                        <div
                          className="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-title-gray-700 menu-icon-gray-500 menu-active-bg menu-state-color fw-semibold py-4 fs-base w-200px me-5"
                          data-kt-element="theme-mode-menu"
                          data-kt-menu="true"
                        >
                          <div className="menu-item px-3 my-0">
                            <div className="menu-content">
                              <span className="fw-bolder text-muted text-uppercase fs-7">
                                {t('header.appearance')}
                              </span>
                            </div>
                          </div>
                          <>
                            <div className="menu-item px-3 my-0">
                              <a
                                className="menu-link px-3 py-2"
                                data-kt-element="mode"
                                data-kt-value="light"
                                href="#"
                              >
                                <span className="menu-icon" data-kt-element="icon">
                                  <Icon icon="ki-night-day" size="fs-2" type="dark" />
                                </span>
                                <span className="menu-title">{t('theme.brightness')}</span>
                              </a>
                            </div>
                            <div className="menu-item px-3 my-0">
                              <a
                                className="menu-link px-3 py-2"
                                data-kt-element="mode"
                                data-kt-value="dark"
                                href="#"
                              >
                                <span className="menu-icon" data-kt-element="icon">
                                  <Icon icon="ki-moon" size="fs-2" type="dark" />
                                </span>
                                <span className="menu-title">{t('theme.dark')}</span>
                              </a>
                            </div>
                            <div className="menu-item px-3 my-0">
                              <a
                                className="menu-link px-3 py-2"
                                data-kt-element="mode"
                                data-kt-value="system"
                                href="#"
                              >
                                <span className="menu-icon" data-kt-element="icon">
                                  <Icon icon="ki-screen" size="fs-2" type="dark" />
                                </span>
                                <span className="menu-title">{t('theme.system')}</span>
                              </a>
                            </div>
                          </>
                          <div className="menu-item px-3 my-0">
                            <div className="menu-content">
                              <span className="fw-bolder text-muted text-uppercase fs-7">
                                {t('profile-settings.title')}
                              </span>
                            </div>
                          </div>
                          <>
                            <div className="menu-item px-3 my-0">
                              <a
                                className="menu-link px-3 py-2"
                                onClick={() => navigate('/profile-settings')}
                              >
                                <span className="menu-icon" data-kt-element="icon">
                                  <Icon icon="ki-profile-circle" size="fs-2" type="dark" />
                                </span>
                                <span className="menu-title">{t('header.title-4')}</span>
                              </a>
                            </div>
                          </>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex flex-center flex-column mbs-profile">
                      <span className="fs-4 text-gray-800 text-hover-primary fw-bold mb-0">
                        <div>
                          {session?.email ? (
                            session.email
                          ) : (
                            <span className="text-muted fs-6">{t('general.no-email')}</span>
                          )}
                        </div>
                      </span>
                      <div className="fw-semibold text-primary mb-7 fs-2">{session.profile}</div>
                      <div className="symbol symbol-90px symbol-circle mb-2">
                        <img alt="profile-picture" src="/assets/media/avatars/av1.png" />
                        <div className="bg-primary position-absolute border border-4 border-body h-20px w-20px rounded-circle translate-middle start-100 top-100 ms-n3 mt-n3"></div>
                        <div className="bg-success position-absolute rounded-circle translate-middle start-100 top-100 border border-4 border-body h-20px w-20px ms-n3 mt-n3" />
                      </div>
                      <span className="fs-1 text-gray-800 text-hover-primary mb-2">
                        ¡{t('general.hi')}, {session.full_name}!
                      </span>
                      <div className="d-flex flex-center flex-wrap mb-5">
                        {session?.profile === PROFILES.ADMIN &&
                          Array.isArray(accounts) &&
                          accounts.length > 1 && (
                            <a
                              className="btn btn-lg btn-light-secondary btn-flex btn-center rounded-left no-border-right w-150px"
                              data-kt-follow-btn="true"
                              onClick={() => {
                                navigate('/select-account');
                              }}
                            >
                              <Icon icon="ki-arrows-loop" size="fs-2x" type="dark" />
                              <span className="indicator-label">{t('asaid.link-accounts')}</span>
                            </a>
                          )}
                        <a
                          className={`btn btn-lg btn-light-danger btn-center ms-2 rounded-right no-border-left ${session?.profile === PROFILES.ADMIN ? 'w-150px' : 'w-200px'}`}
                          onClick={logout}
                        >
                          <Icon icon=" ki-exit-right" size="fs-2x" />
                          <span className="indicator-label">{t('header.title-3')}</span>
                        </a>
                      </div>
                      <DropLanguage />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Header;
